/**
 * How a value got there — the marks every value shows (owner, 2026-10-15;
 * overridden added 2026-10-16, docs/9-exploration/SPEC-value-mark-icon.md):
 *
 *   human          a person set it; no agent was involved
 *   ai_waiting     an agent recommends a value and no person has acted yet
 *   ai_approved    an agent recommended it and a person accepted it
 *   ai_rule        an agent's value accepted by a standing rule (migration 920),
 *                  in the name of the person who set the rule
 *   ai_overridden  a person replaced or changed an agent's value or
 *                  recommendation: changed it before accepting, or replaced an
 *                  agent value that stood (read from accepted_value_revisions,
 *                  migration 821). "Human" is reserved for no agent at all.
 *
 * Plus the AI setting for the value (migration 920, value_ai_modes): off,
 * recommends, or provides — and which agent. Official values only: a session's
 * own value is never shown as the item's (lib/valueScope.ts).
 */

import { isOfficialValue } from './valueScope';
import type { AcceptedValue, ValueProposal } from '../types/prioritization/itemLifecycle';

export type ValueStatus = 'none' | 'human' | 'ai_waiting' | 'ai_approved' | 'ai_rule' | 'ai_overridden';

/**
 * A change that took an official value away (accepted_value_revisions rows with
 * change 'delete' or 'update', official scope only). The app writes a new value
 * as delete-then-insert, so the latest of these for a value says what the
 * current value replaced.
 */
export interface ValueReplacement {
  itemId: string;
  attributeKey: string;
  scaleId: string | null;
  oldValue: unknown;
  oldFromProposalId: string | null;
  changedBy: string | null;
  changedAt: string;
}

/** How a person overrode an agent: changed its recommendation before accepting, or replaced its value later. */
export interface ValueOverride {
  kind: 'changed_before_accepting' | 'replaced';
  /** The agent proposal that was changed or replaced. */
  agentProposal: ValueProposal | null;
  /** The agent's value that did not stand. */
  agentValue: unknown;
  by: string | null;
  at: string | Date | null;
}

export type ValueAiMode = 'off' | 'recommend' | 'provide';

export interface ValueAiSetting {
  workspaceId: string;
  attributeKey: string;
  mode: ValueAiMode;
  agentType: string | null;
  acceptFloor: number;
  setBy: string | null;
  setAt: string;
  reason: string | null;
}

/** True for a value proposal written by any agent (`agent:<type>`). */
export function isAgentProposer(proposedBy: string | null | undefined): boolean {
  return typeof proposedBy === 'string' && proposedBy.startsWith('agent:');
}

export interface ValueStatusResult {
  status: ValueStatus;
  /** The official value read (the latest accepted, any scale). */
  accepted: AcceptedValue | null;
  /** The proposal the accepted value came from, when there was one. */
  fromProposal: ValueProposal | null;
  /** The newest agent recommendation still waiting, even when a value exists. */
  waiting: ValueProposal | null;
  /** Set when status is ai_overridden. */
  override: ValueOverride | null;
}

const isOfficialProposal = (p: ValueProposal) => !p.sessionId && !p.trackId && !p.roundId;
const time = (d: Date | string | undefined) => (d ? new Date(d).getTime() : 0);
const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

export function valueStatus(
  itemId: string,
  attributeKey: string,
  accepted: AcceptedValue[],
  proposals: ValueProposal[],
  replacements: ValueReplacement[] = [],
): ValueStatusResult {
  const official = accepted
    .filter((v) => v.itemId === itemId && v.attributeKey === attributeKey && isOfficialValue(v))
    .sort((a, b) => time(b.acceptedAt) - time(a.acceptedAt))[0] ?? null;

  const mine = proposals.filter((p) => p.itemId === itemId && p.attributeKey === attributeKey);
  const fromProposal = official?.acceptedFromProposalId
    ? mine.find((p) => p.id === official.acceptedFromProposalId) ?? null
    : null;
  const waiting =
    mine
      .filter((p) => p.status === 'proposed' && isAgentProposer(p.proposedBy) && isOfficialProposal(p))
      .sort((a, b) => time(b.proposedAt) - time(a.proposedAt))[0] ?? null;

  let status: ValueStatus;
  let override: ValueOverride | null = null;
  if (official) {
    const fromAgent = !!fromProposal && isAgentProposer(fromProposal.proposedBy);
    // The latest change that took this value away: what the current value replaced.
    const scale = official.scaleId ?? null;
    const last = replacements
      .filter((r) => r.itemId === itemId && r.attributeKey === attributeKey && (r.scaleId ?? null) === scale)
      .sort((a, b) => time(b.changedAt) - time(a.changedAt))[0];
    if (official.acceptanceMethod === 'standing_rule') status = 'ai_rule';
    else if (fromAgent && fromProposal && !same(official.value, fromProposal.proposedValue)) {
      status = 'ai_overridden';
      // An edit keeps the row's proposal link, so a replacement of that same
      // proposal's value means it stood and was changed later; otherwise a
      // person changed the recommendation before accepting it (R-4).
      const later = !!last && last.oldFromProposalId === fromProposal.id;
      override = {
        kind: later ? 'replaced' : 'changed_before_accepting',
        agentProposal: fromProposal,
        agentValue: fromProposal.proposedValue,
        by: later ? last.changedBy : official.acceptedBy ?? null,
        at: later ? last.changedAt : official.acceptedAt ?? null,
      };
    } else if (fromAgent) status = 'ai_approved';
    else {
      const replaced = last?.oldFromProposalId ? mine.find((p) => p.id === last.oldFromProposalId) ?? null : null;
      if (last && replaced && isAgentProposer(replaced.proposedBy)) {
        status = 'ai_overridden';
        override = { kind: 'replaced', agentProposal: replaced, agentValue: last.oldValue, by: last.changedBy, at: last.changedAt };
      } else status = 'human';
    }
  } else {
    status = waiting ? 'ai_waiting' : 'none';
  }
  return { status, accepted: official, fromProposal, waiting, override };
}

export const VALUE_STATUS_LABEL: Record<ValueStatus, string> = {
  none: 'Not set',
  human: 'Human',
  ai_waiting: 'AI recommends · waiting',
  ai_approved: 'AI · approved',
  ai_rule: 'AI · set by rule',
  ai_overridden: 'AI · overridden by a person',
};

export const VALUE_AI_MODE_LABEL: Record<ValueAiMode, string> = {
  off: 'AI off',
  recommend: 'AI recommends',
  provide: 'AI provides',
};
